/**
 * Display helpers for session date/times. Sessions are stored as a naive
 * local datetime ("YYYY-MM-DDTHH:mm:ss") plus an IANA timezone (the backend
 * contract). We render the wall-clock value as-written and append the zone —
 * parsing the parts directly so the output never depends on the browser's
 * local timezone.
 */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface Parts {
  year: string;
  month: number;
  day: string;
  time: string;
}

const parse = (iso: string): Parts | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(iso);
  if (!match) {
    return null;
  }
  return {
    year: match[1],
    month: Number(match[2]),
    day: match[3],
    time: match[4],
  };
};

/** "22 Jun 2026, 14:00" from an ISO-ish "YYYY-MM-DDTHH:mm:ss". */
export const formatSessionDateTime = (iso: string): string => {
  const parts = parse(iso);
  if (!parts) {
    return iso;
  }
  const month = MONTHS[parts.month - 1] ?? String(parts.month);
  return `${parts.day} ${month} ${parts.year}, ${parts.time}`;
};

/** "14:00" time-only from an ISO-ish value. */
export const formatSessionTime = (iso: string): string =>
  parse(iso)?.time ?? iso;

/** "58 min" / "1h 02m" from a duration in seconds. */
export const formatDuration = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};
